import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { cn } from '@/lib/utils';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'default' | 'outline' | 'ghost' | 'destructive' | 'secondary';
  size?: 'sm' | 'md' | 'lg' | 'icon';
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = 'default', size = 'md', ...props }, ref) => {
    return (
      <button
        ref={ref}
        className={cn(
          'inline-flex items-center justify-center gap-1.5 font-medium transition-colors focus:outline-none focus-visible:ring-1 focus-visible:ring-accent focus-visible:ring-offset-1 focus-visible:ring-offset-bg-base disabled:pointer-events-none disabled:opacity-40',
          {
            // Default — solid copper
            'bg-accent text-white hover:bg-accent-hover rounded-sm':
              variant === 'default',
            // Outline — bordered, transparent fill
            'border border-border bg-transparent text-text-secondary hover:bg-bg-elevated hover:text-text-primary rounded-sm':
              variant === 'outline',
            // Ghost — no border
            'bg-transparent text-text-secondary hover:bg-bg-elevated hover:text-text-primary rounded-sm':
              variant === 'ghost',
            // Destructive
            'border border-status-red/30 bg-status-red/5 text-status-red hover:bg-status-red/10 rounded-sm':
              variant === 'destructive',
            // Secondary
            'border border-border bg-bg-elevated text-text-primary hover:bg-bg-surface rounded-sm':
              variant === 'secondary',
          },
          {
            'h-7 px-2.5 text-xs':  size === 'sm',
            'h-8 px-3.5 text-sm':  size === 'md',
            'h-10 px-5 text-base': size === 'lg',
            'h-8 w-8 p-0':         size === 'icon',
          },
          className,
        )}
        {...props}
      />
    );
  },
);

Button.displayName = 'Button';
export { Button };
